import * as React from 'react'
import { OTPInput, OTPInputContext } from 'input-otp'
import { Minus } from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * Figma (node 81:141 — InputOTP):
 *
 * Variants: Simple (3+3), Digits Only (6 connected), With Separator (2+2+2), With Spacing (6 spaced)
 * Slot: size-8 (32px), rounded-md (8px), border border-input, bg-background
 * Focus: ring-1 ring-ring
 * Invalid: border-destructive, ring-destructive/20
 * Separator: Minus icon, size-6 (24px)
 * Connected slots: first rounded-l-md, last rounded-r-md, middle rounded-none border-l-0
 */

export type IntuiInputOTPProps = React.ComponentPropsWithoutRef<typeof OTPInput>

const IntuiInputOTP = React.forwardRef<
  React.ElementRef<typeof OTPInput>,
  IntuiInputOTPProps
>(({ className, containerClassName, ...props }, ref) => (
  <OTPInput
    ref={ref}
    containerClassName={cn(
      'flex items-center has-[:disabled]:opacity-50',
      containerClassName,
    )}
    className={cn('disabled:cursor-not-allowed', className)}
    {...props}
  />
))
IntuiInputOTP.displayName = 'IntuiInputOTP'

/* ─── Group ─── */
const IntuiInputOTPGroup = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { invalid?: boolean }
>(({ className, invalid, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'flex items-center',
      invalid && 'rounded-md ring-[3px] ring-destructive/20',
      className,
    )}
    {...props}
  />
))
IntuiInputOTPGroup.displayName = 'IntuiInputOTPGroup'

/* ─── Slot ─── */
interface IntuiInputOTPSlotProps extends React.HTMLAttributes<HTMLDivElement> {
  index: number
  invalid?: boolean
}

const IntuiInputOTPSlot = React.forwardRef<HTMLDivElement, IntuiInputOTPSlotProps>(
  ({ index, className, invalid, ...props }, ref) => {
    const inputOTPContext = React.useContext(OTPInputContext)
    const { char, hasFakeCaret, isActive } = inputOTPContext.slots[index]

    return (
      <div
        ref={ref}
        className={cn(
          'relative flex size-8 items-center justify-center border-y border-r border-input text-sm transition-all',
          'first:rounded-l-md first:border-l last:rounded-r-md',
          isActive && 'z-10 ring-1 ring-ring',
          invalid && 'border-destructive',
          className,
        )}
        {...props}
      >
        {char}
        {hasFakeCaret && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="h-4 w-px animate-caret-blink bg-foreground duration-1000" />
          </div>
        )}
      </div>
    )
  },
)
IntuiInputOTPSlot.displayName = 'IntuiInputOTPSlot'

/* ─── Spaced Slot (individual rounded borders, for "With Spacing" variant) ─── */
const IntuiInputOTPSpacedSlot = React.forwardRef<HTMLDivElement, IntuiInputOTPSlotProps>(
  ({ index, className, invalid, ...props }, ref) => {
    const inputOTPContext = React.useContext(OTPInputContext)
    const { char, hasFakeCaret, isActive } = inputOTPContext.slots[index]

    return (
      <div
        ref={ref}
        className={cn(
          'relative flex size-8 items-center justify-center rounded-md border border-input text-sm transition-all',
          isActive && 'z-10 ring-1 ring-ring',
          invalid && 'border-destructive ring-[3px] ring-destructive/20',
          className,
        )}
        {...props}
      >
        {char}
        {hasFakeCaret && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="h-4 w-px animate-caret-blink bg-foreground duration-1000" />
          </div>
        )}
      </div>
    )
  },
)
IntuiInputOTPSpacedSlot.displayName = 'IntuiInputOTPSpacedSlot'

/* ─── Separator ─── */
const IntuiInputOTPSeparator = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ ...props }, ref) => (
  <div ref={ref} role="separator" className="flex items-center" {...props}>
    <Minus className="size-6 text-muted-foreground" />
  </div>
))
IntuiInputOTPSeparator.displayName = 'IntuiInputOTPSeparator'

export {
  IntuiInputOTP,
  IntuiInputOTPGroup,
  IntuiInputOTPSlot,
  IntuiInputOTPSpacedSlot,
  IntuiInputOTPSeparator,
}
